import { BlurView } from 'expo-blur';
import * as Haptics from 'expo-haptics';
import { ImpactFeedbackStyle } from 'expo-haptics';
import {
  createContext,
  forwardRef,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dimensions,
  EasingFunction,
  Modal,
  Platform,
  StatusBar,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import 'react-native-gesture-handler';
import Animated, {
  Easing,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';
import useBehaviorSubject from '../../functions/hooks/useBehaviorSubject';
import { useCreation } from '../../functions/hooks/useCreation';
import { usePersistFn } from '../../functions/hooks/usePersistFn';
import { absoluteFillCss } from '../styles/css';

// const HOLD_ITEM_TRANSFORM_DURATION = 4000;
const HOLD_ITEM_TRANSFORM_DURATION = 150;

export type OverlayComponentProps = {
  matchItemWidth?: boolean;
  onDismiss?: () => void;
  coordinates: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  component: (props: {
    close: (
      callback?: () => void,
      options?: { duration?: number; easing?: EasingFunction },
    ) => void;
    replaceContent: (newContent: ReactNode) => void;
  }) => ReactElement;
  componentPos?: Partial<{
    left: number;
    top: number;
    bottom: number;
    right: number;
  }>;
  children?: ReactNode;
  background: 'blur' | 'transparent';
};

export const HoldItemContext = createContext<{
  present$: BehaviorSubject<OverlayComponentProps | undefined>;
}>(null as any);

const HoldItemOverlay = memo(
  (
    props: OverlayComponentProps & {
      onDismiss: () => void;
    },
  ) => {
    const alpha = useSharedValue(0);
    const pressInZoom = useSharedValue(1);
    const style = useAnimatedStyle(() => ({
      opacity: alpha.value,
    }));
    const scaleOffset = useSharedValue({
      x: 0,
      y: 0,
    });
    const overlayPos = props.componentPos ?? {
      left: props.coordinates.left,
      top: props.coordinates.top + props.coordinates.height + 5,
    };
    const toLeft = overlayPos.left != null;
    const toTop = overlayPos.top != null;

    const replacementStyle = useAnimatedStyle(() => ({
      opacity: alpha.value,
    }));

    const overlayStyle = useAnimatedStyle(() => ({
      opacity: alpha.value,
      width: props.matchItemWidth ? props.coordinates.width : undefined,
      transform: [
        {
          translateY: interpolate(
            alpha.value,
            [0, 1],
            [scaleOffset.value.y, 0],
          ),
        },
        {
          translateX: interpolate(
            alpha.value,
            [0, 1],
            [scaleOffset.value.x, 0],
          ),
        },
        {
          scale: alpha.value,
        },
      ],
    }));

    const dismiss = useCallback(
      (
        callback?: () => void,
        // allow the user to specify in which way to dismiss the menu
        // sometimes, we want to dismiss the menu much faster before
        // presenting the follow-up menu in a short time.
        options?: { duration?: number; easing?: EasingFunction },
      ) => {
        alpha.value = withTiming(
          0,
          {
            duration:
              options?.duration ?? 300 /* default value by reanimated */,
            // use the reanimated default in-out quad easing if not
            // specified, undefined will crash the app.
            easing: options?.easing ?? Easing.inOut(Easing.quad),
          },
          isFinished => {
            if (isFinished) {
              runOnJS(props.onDismiss)();
              if (callback) {
                runOnJS(callback)();
              }
            }
          },
        );
      },
      [alpha, props.onDismiss],
    );
    const [contentOverwrite, setContentOverwrite] = useState<ReactNode>();
    const replaceContent = useCallback(
      (content: ReactNode) => {
        const swap = () => setContentOverwrite(content);
        alpha.value = withTiming(0, {}, () => {
          runOnJS(swap)();
          alpha.value = withTiming(1);
        });
      },
      [alpha],
    );
    const { top } = useSafeAreaInsets();
    return (
      <Modal animationType="none" transparent={true} visible={true}>
        <View
          css={`
            ${absoluteFillCss};
            top: ${Platform.OS === 'android' ? top : 0}px;
          `}
        >
          <TouchableWithoutFeedback onPress={() => dismiss()}>
            <Animated.View style={[StyleSheet.absoluteFill, style]}>
              {props.background === 'blur' ? (
                <BlurView
                  style={StyleSheet.absoluteFill}
                  intensity={100}
                  tint="dark"
                />
              ) : (
                <View style={StyleSheet.absoluteFill} />
              )}
            </Animated.View>
          </TouchableWithoutFeedback>
          {props.background === 'blur' && (
            <Animated.View
              style={[
                {
                  ...props.coordinates,
                  position: 'absolute',
                },
                replacementStyle,
              ]}
            >
              {props.children}
            </Animated.View>
          )}
          <Animated.View
            onLayout={event => {
              const { height, width } = event.nativeEvent.layout;
              scaleOffset.value = {
                x: toLeft ? -width / 2 : width / 2,
                y: toTop ? -height / 2 : height / 2,
              };
              alpha.value = withTiming(1, {
                duration: HOLD_ITEM_TRANSFORM_DURATION,
              });
              pressInZoom.value = withTiming(1, {
                duration: HOLD_ITEM_TRANSFORM_DURATION,
              });
            }}
            style={[{ position: 'absolute' }, overlayPos, overlayStyle]}
          >
            {contentOverwrite ?? (
              <props.component
                close={dismiss}
                replaceContent={replaceContent}
              />
            )}
          </Animated.View>
        </View>
      </Modal>
    );
  },
);

const HoldItemOverlayWrapper = memo(() => {
  const { present$ } = useContext(HoldItemContext);
  const [state, setState] = useBehaviorSubject(present$);
  if (state == null) {
    return null;
  }
  return (
    <HoldItemOverlay
      {...state}
      onDismiss={() => {
        state.onDismiss?.();
        setState(undefined);
      }}
    >
      {state.children}
    </HoldItemOverlay>
  );
});

export const HoldItemProvider = (props: {
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
}) => {
  const present$ = useCreation(
    () => new BehaviorSubject<OverlayComponentProps | undefined>(undefined),
    [],
  );
  const value = useMemo(() => ({ present$ }), [present$]);
  return (
    <HoldItemContext.Provider value={value}>
      {props.children}
      <HoldItemOverlayWrapper />
    </HoldItemContext.Provider>
  );
};

const offsetFix = () => {
  let offsetY = 0;
  if (Platform.OS === 'android') {
    const screenSize = Dimensions.get('screen');
    const windowSize = Dimensions.get('window');
    const statusBarHeight = StatusBar.currentHeight ?? 0;
    const navbarHeight = screenSize.height - windowSize.height;
    offsetY = navbarHeight - statusBarHeight;
  }
  return offsetY;
};

export const defaultPosStrategyFactory =
  (options?: { matchItemWidth?: boolean; menuSize?: number }) =>
  (
    box: {
      left: number;
      top: number;
      width: number;
      height: number;
    },
    screen: { width: number; height: number },
    menuSize?: { width?: number; height?: number },
  ): Partial<{ left: number; right: number; top: number; bottom: number }> => {
    const menuWidth =
      menuSize?.width ?? (options?.matchItemWidth ? box.width : 200);
    const menuHeight = options?.menuSize ?? menuSize?.height ?? 200;

    console.log({
      screenHeight: Dimensions.get('screen').height,
      windowHeight: Dimensions.get('window').height,
      statusBarHeight: StatusBar.currentHeight ?? 0,
    });
    if (box.top > screen.height - menuHeight - offsetFix()) {
      if (box.left + box.width > screen.width - menuWidth) {
        return {
          right: screen.width - box.left - box.width,
          bottom: screen.height - box.top + 5 - offsetFix(),
        };
      }
      return {
        left: box.left,
        bottom: screen.height - box.top + 5 - offsetFix(),
      };
    }
    if (box.left + box.width > screen.width - menuWidth) {
      return {
        right: screen.width - box.left - box.width,
        top: box.top + box.height + 5,
      };
    }
    return { left: box.left, top: box.top + box.height + 5 };
  };

export const defaultPosStrategy = defaultPosStrategyFactory();

export const stickToTopLeftPosStrategy = (
  box: {
    left: number;
    top: number;
    width: number;
    height: number;
  },
  screen: { width: number; height: number },
): Partial<{ left: number; right: number; top: number; bottom: number }> => {
  return { left: box.left, bottom: screen.height - box.top + 5 };
};

export const stickToBottomRightPosStrategy = (
  box: {
    left: number;
    top: number;
    width: number;
    height: number;
  },
  screen: { width: number; height: number },
): Partial<{ left: number; right: number; top: number; bottom: number }> => {
  return {
    right: screen.width - box.left - box.width,
    bottom: screen.height - box.top + 5 - offsetFix(),
  };
};

export type HoldItemProps = {
  children?: ReactNode;
  component: OverlayComponentProps['component'];
  posStrategy?: typeof defaultPosStrategy;
  matchItemWidth?: boolean;
  style?: StyleProp<ViewStyle>;
  backgroundMode?: 'blur' | 'transparent';
  menuSize?: { width?: number; height?: number };
  onDismiss?: () => void;
};

export const HoldItem = forwardRef<{ present: () => void }, HoldItemProps>(
  (props, ref) => {
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();
    const { present$ } = useContext(HoldItemContext);
    const box = useRef<View>(null);

    const presentMenu = usePersistFn(() => {
      box.current?.measureInWindow((left, top, width, height) => {
        const coordinates = {
          left,
          top,
          width,
          height,
        };

        // todo(andy): it's very strange that w/o these two console logs
        // the menu presentation sometimes does not work as expected.
        // need to inspect the real problem w/ the hold item implementation.
        console.log('hold item coordinates: ', {
          left,
          top,
          width,
          height,
        });
        const position = (props.posStrategy ?? defaultPosStrategy)(
          coordinates,
          {
            height: screenHeight,
            width: screenWidth,
          },
          props.menuSize,
        );
        console.log('hold menu position: ', position);

        present$.next({
          children: props.children,
          coordinates: coordinates,
          componentPos: position,
          component: props.component,
          background: props.backgroundMode ?? 'blur',
          matchItemWidth: props.matchItemWidth,
          onDismiss: props.onDismiss,
        });

        Haptics.impactAsync(ImpactFeedbackStyle.Light).catch(() => null);
      });
    });

    useImperativeHandle(
      ref,
      () => ({
        present: presentMenu,
      }),
      [presentMenu],
    );

    /*https://reactnative.dev/docs/view#collapsable*/
    /*Without this, the measure won't work on android*/
    return (
      <View style={props.style} collapsable={false} ref={box}>
        {props.children}
      </View>
    );
  },
);
